import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard,
  BarChart3,
  MessageSquareHeart,
  Handshake,
  Home,
  LogOut,
  Menu,
  X,
  Sparkles,
  DollarSign,
  ShoppingBag,
  Mail,
  KeyRound,
  Gift,
  Send,
  Heart,
  Compass,
  Beaker,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminShell — the layout used by every /admin/* page.
 *
 * Visually distinct from the consumer app (which is warm cream, soft
 * serif editorial): this uses a deep charcoal sidebar with gold
 * accents and tighter data-dense typography. Think "operations
 * console," not "beauty magazine."
 *
 * Drop it around any admin page:
 *
 *   <AdminShell title="Analytics">
 *     <MyDashboardContent />
 *   </AdminShell>
 */

const NAV = [
  { to: "/admin", label: "Overview", Icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", Icon: KeyRound },
  { to: "/admin/revenue", label: "Revenue", Icon: DollarSign },
  { to: "/admin/products", label: "Products", Icon: ShoppingBag },
  { to: "/admin/brand-discovery", label: "Brand discovery", Icon: Compass },
  { to: "/admin/brand-catalog-sync", label: "Catalog sync", Icon: Beaker },
  { to: "/admin/analytics", label: "Analytics", Icon: BarChart3 },
  { to: "/admin/feedback", label: "Feedback", Icon: MessageSquareHeart },
  { to: "/admin/broadcasts", label: "Broadcasts", Icon: Send },
  { to: "/admin/waitlist", label: "Waitlist", Icon: Mail },
  { to: "/admin/members", label: "Members", Icon: KeyRound },
  { to: "/admin/gift-codes", label: "Gift codes", Icon: Gift },
  { to: "/admin/ambassadors", label: "Ambassadors", Icon: Heart },
];

const AdminSidebar = ({ user, onClose }) => {
  const navigate = useNavigate();

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore — we want to clear client state regardless
    }
    window.location.replace("/");
  };

  return (
    <aside
      className="admin-sidebar w-64 shrink-0 bg-[#0F1115] border-r border-white/5 flex flex-col"
      data-testid="admin-sidebar"
    >
      {/* Brand block */}
      <div className="px-6 py-6 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D4B483] to-[#9B6A6C] flex items-center justify-center shadow-[0_4px_12px_rgba(212,180,131,0.25)]">
            <Sparkles className="h-4.5 w-4.5 text-[#1A1720]" />
          </div>
          <div>
            <div className="font-serif italic text-lg text-white leading-none">
              TruBeauté
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B483] mt-0.5">
              Admin console
            </div>
          </div>
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden text-white/60 hover:text-white p-1.5"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Primary nav */}
      <nav className="flex-1 px-3 space-y-1" data-testid="admin-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClose}
            className={({ isActive }) =>
              isActive
                ? "flex items-center gap-3 rounded-xl bg-white/[0.06] text-white px-3.5 py-2.5 text-sm font-medium border border-white/10"
                : "flex items-center gap-3 rounded-xl text-white/60 hover:text-white hover:bg-white/[0.04] px-3.5 py-2.5 text-sm transition-colors"
            }
            data-testid={`admin-nav-${item.label.toLowerCase()}`}
          >
            <item.Icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}

        <div className="pt-6 pb-2 px-3.5 text-[10px] uppercase tracking-[0.24em] text-white/30">
          External
        </div>
        <Link
          to="/"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl text-white/60 hover:text-white hover:bg-white/[0.04] px-3.5 py-2.5 text-sm transition-colors"
          data-testid="admin-nav-to-webapp"
        >
          <Home className="h-4 w-4" />
          Back to webapp
        </Link>
      </nav>

      {/* Footer — user + sign out */}
      <div className="border-t border-white/5 px-4 py-4">
        {user && (
          <div className="mb-3 px-1.5">
            <div className="text-xs text-white/50 uppercase tracking-[0.2em]">
              Signed in as
            </div>
            <div
              className="text-sm text-white truncate mt-0.5"
              data-testid="admin-user-email"
              title={user.email}
            >
              {user.email}
            </div>
          </div>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.09] text-white/80 hover:text-white px-3.5 py-2.5 text-sm transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

const AdminShell = ({ title, subtitle, eyebrow, actions, children }) => {
  const { user, loading } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (loading) {
    return (
      <div className="admin-console min-h-screen bg-[#0F1115] flex items-center justify-center text-white/60">
        Loading…
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return (
      <div className="admin-console min-h-screen bg-[#0F1115] text-white">
        <main className="max-w-xl mx-auto pt-32 pb-24 px-6 text-center">
          <div className="h-12 w-12 rounded-full bg-white/[0.06] flex items-center justify-center mx-auto mb-5 border border-white/10">
            <Handshake className="h-6 w-6 text-[#D4B483]" />
          </div>
          <h1 className="text-3xl font-light mb-2">
            This area is admin-only.
          </h1>
          <p className="text-white/60 mb-6">
            If you think you should have access, the founder can add your
            email to the <code className="bg-white/10 px-1.5 py-0.5 rounded text-[#D4B483]">DEV_EMAILS</code> allowlist.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] rounded-full px-6 py-3 transition-colors"
            data-testid="admin-no-access-home-btn"
          >
            Back to webapp
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-console min-h-screen bg-[#141820] text-white flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <AdminSidebar user={user} />
      </div>

      {/* Mobile sidebar drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="relative z-50 flex">
            <AdminSidebar
              user={user}
              onClose={() => setMobileNavOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between gap-4 px-6 lg:px-10 py-5 border-b border-white/5 bg-[#0F1115]/80 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden text-white/70 hover:text-white"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            {eyebrow && (
              <span className="hidden md:inline text-[10px] uppercase tracking-[0.28em] text-[#D4B483]">
                {eyebrow}
              </span>
            )}
            <h1
              className="text-lg font-medium text-white truncate"
              data-testid="admin-page-title"
            >
              {title}
            </h1>
          </div>
          {actions && (
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
          )}
        </header>

        {subtitle && (
          <div className="px-6 lg:px-10 py-4 border-b border-white/5 text-sm text-white/60 leading-relaxed">
            {subtitle}
          </div>
        )}

        {/* Page body */}
        <main
          className="flex-1 px-6 lg:px-10 py-8 overflow-x-hidden"
          data-testid="admin-main"
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
